// BUG-2566 (TIC-12088): which server registration rows Close Till's "LO" releases.
//
// LO (upsertPOSActivity type LO) sets the till AVL / linked N and makes ONLY the
// given cwr_tillreg_history row inactive; getTills returns the active rows, and
// tillRegistration refuses a till that still has one ("already linked").
//
// Before this change Close Till sent LO for db.tillRegistrations row [0]. With an
// empty table (KWC CV - POS, 10-01..10-07) nothing was sent and the till stayed
// In Use on the server; with rows from several tills, [0] could be another till
// (TIC-10115). Now:
//   1. the local row of the till being closed (newest by regTimestamp),
//   2. else, once upsertTill has reached the server, the closing till's active
//      rows from getTills -- never a row another device marked as its own
//      (BUG-2443 "CWPOS-" sessionid), and on restaurant (layout 2) / PRD tills,
//      which may be registered on several devices, only this device's own row.

// Same key / marker as utils/tillDeviceRegistration.js (BUG-2443). Read here without
// creating an id: Close Till must not mint a device id.
const POS_DEVICE_ID_KEY = "cw_pos_device_id";
const POS_DEVICE_MARKER = "CWPOS-";

const isDeviceMarker = (value) => typeof value === "string" && value.startsWith(POS_DEVICE_MARKER);

export function readOwnDeviceId() {
  try {
    const id = localStorage.getItem(POS_DEVICE_ID_KEY);
    return isDeviceMarker(id) ? id : null;
  } catch {
    return null;
  }
}

const accessOf = (obj) => (Array.isArray(obj?.tillAccess) ? obj.tillAccess[0] : obj?.tillAccess);

// The till being closed, from the signed-in till (useAuthStore tillData).
export function closingTillOf(tillData) {
  const access = accessOf(tillData);
  const cwrTill = access?.cwrTill;
  if (!cwrTill) return null;
  return {
    cwrTillID: cwrTill.cwrTillID || null,
    till: cwrTill.till || null,
    searchKey: cwrTill.searchKey || null,
    csBunitId: access?.csBunit?.csBunitId || null,
    csUserId: access?.csUserId || access?.csUser?.csUserId || null,
  };
}

// One LO from a db.tillRegistrations row (the tillRegistration response). null when
// a field the mutation needs is missing.
export function releaseFromRow(row) {
  const access = accessOf(row);
  const tillRegistrationId = row?.tillHistory?.[0]?.cwrTillRegHistoryId;
  const csBunitId = access?.csBunit?.csBunitId;
  const csUserId = access?.csUser?.csUserId;
  if (!tillRegistrationId || !csBunitId || !csUserId) return null;
  return { tillRegistrationId, csBunitId, csUserId, till: access?.cwrTill?.till || null };
}

const rowTillOf = (row) => {
  const cwrTill = accessOf(row)?.cwrTill || {};
  return { till: cwrTill.till || null, searchKey: cwrTill.searchKey || row?.searchKey || null };
};

const sameTill = (a, b) => {
  if (a.searchKey && b.searchKey) return a.searchKey === b.searchKey;
  return !!(a.till && b.till && a.till === b.till);
};

const regTimestampOf = (row) => String(row?.tillHistory?.[0]?.regTimestamp || "");

// Step 1: from this device's db.tillRegistrations rows.
//   source "local"           newest row of the closing till
//   source "local_first_row" closing till unknown, or row [0] names no till: row [0] (as before)
//   source "none"            no usable row of the closing till
export function resolveLocalRelease(rows, closingTill) {
  const list = (Array.isArray(rows) ? rows : []).filter(Boolean);
  const firstRow = () => {
    const first = releaseFromRow(list[0]);
    return first ? { releases: [first], source: "local_first_row" } : { releases: [], source: "none" };
  };
  if (!(closingTill?.till || closingTill?.searchKey)) return firstRow();
  let best = null;
  list.forEach((row) => {
    if (!sameTill(rowTillOf(row), closingTill)) return;
    const release = releaseFromRow(row);
    if (!release) return;
    // Newest registration wins; on a tie the later row (appended later).
    if (!best || regTimestampOf(row) >= best.ts) best = { release, ts: regTimestampOf(row) };
  });
  if (best) return { releases: [best.release], source: "local" };
  // Row [0] that names no till cannot be told apart: keep the old behaviour.
  const first = rowTillOf(list[0] || {});
  if (list[0] && !first.till && !first.searchKey) return firstRow();
  return { releases: [], source: "none" };
}

// Step 2: from getTills (this user's tills with their ACTIVE registration rows).
//   own rows (this device's id)                      -> always
//   unmarked rows (older POS, no device id)          -> only on a single-device till
//   rows another device marked as its own (BUG-2443) -> never
export function resolveServerRelease({ tills, closingTill, deviceId, multiDevice }) {
  const till = (Array.isArray(tills) ? tills : []).find((t) => t?.cwrTillID && t.cwrTillID === closingTill?.cwrTillID);
  if (!till) return { releases: [], source: "server", reason: "till_not_listed" };
  const csBunitId = closingTill.csBunitId || till.cSBunitID || null;
  const csUserId = closingTill.csUserId || null;
  if (!csBunitId || !csUserId) return { releases: [], source: "server", reason: "no_user_or_store" };
  const active = (Array.isArray(till.tillHistory) ? till.tillHistory : []).filter((r) => r?.cwrTillRegHistoryId);
  if (active.length === 0) return { releases: [], source: "server", reason: "no_active_row" };
  const toRelease = (r) => ({ tillRegistrationId: r.cwrTillRegHistoryId, csBunitId, csUserId, till: till.till || closingTill.till || null });
  const own = deviceId ? active.filter((r) => r.sessionid === deviceId) : [];
  const foreign = active.filter((r) => isDeviceMarker(r.sessionid) && r.sessionid !== deviceId).length;
  if (own.length > 0) return { releases: own.map(toRelease), source: "server", reason: "own_rows", foreign };
  if (multiDevice) return { releases: [], source: "server", reason: "multi_device_till", foreign, active: active.length };
  const unmarked = active.filter((r) => !isDeviceMarker(r.sessionid));
  if (unmarked.length === 0) return { releases: [], source: "server", reason: "only_other_device_rows", foreign };
  return { releases: unmarked.map(toRelease), source: "server", reason: "unmarked_rows", foreign };
}

// The shape upsertPOSActivity reads (a db.tillRegistrations row).
export function toActivityRow(release) {
  return {
    tillAccess: [{ csBunit: { csBunitId: release.csBunitId }, csUser: { csUserId: release.csUserId } }],
    tillHistory: [{ cwrTillRegHistoryId: release.tillRegistrationId }],
  };
}

export const isActivityOk = (res) => {
  const status = res?.data?.data?.upsertPOSActivity?.status;
  return status === 200 || status === "200";
};

// Support can read this from CW Browser's observability pipeline; on the web it
// stays in the console and the debug trace.
export function logTillLifecycle(level, message, data) {
  try {
    window.electronAPI?.observability?.log(level, "till-lifecycle", message, data);
  } catch (err) {
    console.warn("[TillLogoutRelease] observability log failed", err?.message);
  }
  if (level === "error" || level === "warn") console.warn(`[TillLogoutRelease] ${message}`, data);
}
